import React, { useState, useEffect } from 'react';

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';

const INPUT_STYLE = {
  width: '100%',
  padding: '9px 12px',
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  fontSize: 14,
  boxSizing: 'border-box',
  outline: 'none',
  background: '#fff',
};

const LABEL_STYLE = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  color: '#555',
  marginBottom: 5,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

export default function CreateSprintModal({ isOpen, onClose, onSave, projectId, editSprint = null }) {
  const EMPTY = {
    name: '',
    startDate: '',
    endDate: '',
    goal: '',
    velocity: 20,
  };

  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    if (editSprint) {
      setForm({
        name: editSprint.name || '',
        startDate: editSprint.startDate || '',
        endDate: editSprint.endDate || '',
        goal: editSprint.goal || '',
        velocity: editSprint.velocity ?? 20,
      });
    } else {
      setForm(EMPTY);
    }
  }, [isOpen, editSprint]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleSave = () => {
    if (!form.name.trim()) { setError('Sprint name is required.'); return; }
    if (!form.startDate || !form.endDate) { setError('Start and end dates are required.'); return; }
    if (form.endDate < form.startDate) { setError('End date must be after start date.'); return; }
    onSave({
      projectId,
      name: form.name.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
      goal: form.goal.trim(),
      velocity: parseInt(form.velocity) || 20,
    });
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          width: 440,
          maxWidth: '95vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: NAVY }}>
            {editSprint ? 'Edit Sprint' : 'New Sprint'}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && (
            <p style={{ margin: 0, color: '#E96A6A', fontSize: 13, fontWeight: 600 }}>{error}</p>
          )}

          <div>
            <label style={LABEL_STYLE}>Sprint Name *</label>
            <input
              style={INPUT_STYLE}
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Sprint 2: Auth & Dashboard"
              autoFocus
            />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={LABEL_STYLE}>Start Date *</label>
              <input
                type="date"
                style={INPUT_STYLE}
                value={form.startDate}
                onChange={e => set('startDate', e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={LABEL_STYLE}>End Date *</label>
              <input
                type="date"
                style={INPUT_STYLE}
                value={form.endDate}
                onChange={e => set('endDate', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label style={LABEL_STYLE}>Sprint Goal</label>
            <textarea
              style={{ ...INPUT_STYLE, minHeight: 64, resize: 'vertical' }}
              value={form.goal}
              onChange={e => set('goal', e.target.value)}
              placeholder="What should be achieved by the end of this sprint?"
            />
          </div>

          <div>
            <label style={LABEL_STYLE}>Velocity Target (story points)</label>
            <input
              type="number"
              style={INPUT_STYLE}
              value={form.velocity}
              onChange={e => set('velocity', e.target.value)}
              min="0"
              placeholder="20"
            />
          </div>
        </div>

        <div style={{
          padding: '14px 24px',
          borderTop: `1px solid ${BORDER}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 18px', borderRadius: 8,
              border: `1px solid ${BORDER}`, background: '#fff',
              cursor: 'pointer', fontSize: 14, color: '#555',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 20px', borderRadius: 8,
              border: 'none', background: NAVY,
              color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700,
            }}
          >
            {editSprint ? 'Save Changes' : 'Create Sprint'}
          </button>
        </div>
      </div>
    </div>
  );
}
